import { ref, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDeviceStore } from '@/stores'
import { getImageUrl } from '@/utils/assets'
import { defaultReportError } from '@/composables/useErrorLogger'
import { getPictures } from '../utils/screenPictureDb'
import {
  createEmptyPages,
  enrichPageItems,
  getBuiltInScreenPictures,
  getHomePresets,
  refreshPageDisplay,
  resolveBaseScreenPageConfig,
} from '../utils/pages'

import type { ScreenPictureItem, PageItem, PresetItem } from '../utils/types'

/** 设备的屏显全长度列表（由 manifest 的 maxScreenSlots 决定）。 */
const pageConfig = shallowRef(resolveBaseScreenPageConfig())
const pages = ref<PageItem[]>(createEmptyPages(pageConfig.value.maxScreenSlots))
/** 首页屏显可选的预设图片 URL 列表。 */
const homePresets = ref<PresetItem[]>([])
/** 设备当前使用中的首页固件样式值。 */
const activeHomePresetStyle = ref<number | null>(null)
/** 本地素材库列表（含 blob 预览 URL）。 */
const pictures = ref<ScreenPictureItem[]>([])
/** 当前选中的屏显槽位。 */
const activePage = ref<number>(pageConfig.value.homeSlot)
/** 共享初始化或写入设备时的 loading 状态。 */
const loading = ref(false)

/** 当前设备 init 进行中的 Promise，用于并发调用去重。 */
let initPromise: Promise<void> | null = null
/** 已完成初始化的设备 ID。 */
let initializedDeviceId: string | null = null
/** 是否已注册 currentDevice 监听，保证全局只 watch 一次。 */
let deviceWatcherStarted = false

/** 获取当前底座设备的屏显 feature。 */
function getBaseScreen() {
  const deviceStore = useDeviceStore()
  return deviceStore.currentDevice?.features.baseScreen
}

/** 释放素材库中不再使用的 blob 预览 URL。 */
function revokePictureUrls(items: ScreenPictureItem[]) {
  items.forEach((item) => item.url?.startsWith('blob:') && URL.revokeObjectURL(item.url))
}

/** 切设备或离开底座时重置共享状态，并释放 blob 资源。 */
function reset(manifest?: Parameters<typeof resolveBaseScreenPageConfig>[0]) {
  revokePictureUrls(pictures.value)
  pageConfig.value = resolveBaseScreenPageConfig(manifest)
  pages.value = createEmptyPages(pageConfig.value.maxScreenSlots)
  homePresets.value = []
  activeHomePresetStyle.value = null
  pictures.value = []
  activePage.value = pageConfig.value.homeSlot
}

/** 组装 enrich / refresh 屏显列表时所需的展示上下文。 */
function getPageDisplayOptions() {
  return {
    pageConfig: pageConfig.value,
    assetsDir: useDeviceStore().currentDevice?.manifest.ui.assetsDir,
    homePreset: homePresets.value.find((preset) => preset.style === activeHomePresetStyle.value)
      ?.thumbnail,
    pictures: pictures.value,
  }
}

/** 共享 pages 是否已从设备加载过（非 createEmptyPages 占位数据）。 */
function hasLoadedPages() {
  return pages.value.some((page) => page.slot >= 0)
}

/** 从设备回读屏显页面数组，并补充缩略图等 UI 字段。 */
async function syncPages() {
  const baseScreen = getBaseScreen()
  if (!baseScreen) return

  try {
    const devicePages = await baseScreen.pages.getPages()
    pages.value = enrichPageItems(devicePages, getPageDisplayOptions())
  } catch (err) {
    console.error('Failed to sync device pages', err)
    defaultReportError(`Failed to sync device pages: ${JSON.stringify(err)}`)
  }
}

/**
 * 刷新屏显 pages。
 * 首次进入时复用初始化 Promise；后续每次进入页面都重新从设备 getPages，
 * 避免 pages / player 路由使用上一次进入时的共享快照。
 */
async function refreshPages() {
  const deviceStore = useDeviceStore()
  const device = deviceStore.currentDevice

  if (!device?.features.baseScreen || device.manifest.type !== 'base') {
    return
  }

  loading.value = true
  try {
    if (initializedDeviceId !== device.id || !hasLoadedPages()) {
      await ensureInitialized()
      return
    }

    await syncPages()
  } finally {
    loading.value = false
  }
}

/** 从 baseScreen capability 的首页 system page 读取预设资源。 */
function loadHomePresets() {
  const deviceStore = useDeviceStore()
  const assetsDir = deviceStore.currentDevice?.manifest.ui.assetsDir
  const presets = getHomePresets(pageConfig.value)

  if (!assetsDir) {
    homePresets.value = []
    return
  }

  homePresets.value = presets.map((preset) => ({
    ...preset,
    thumbnail: getImageUrl(`${assetsDir}/${preset.asset}`),
  }))
}

/** 读取设备当前使用的首页预设索引。 */
async function loadActiveHomePresetStyle() {
  const baseScreen = getBaseScreen()
  if (!baseScreen) return

  try {
    activeHomePresetStyle.value = await baseScreen.getIndexStyle()
  } catch (err) {
    console.error('Failed to get homepage screen')
    defaultReportError(`Failed to get homepage screen: ${JSON.stringify(err)}`)
  }
}

/**
 * 从 IndexedDB 加载本地素材库，并刷新 pages 中的缩略图引用。
 * 新建 blob URL 后会释放上一批 URL，避免内存泄漏。
 */
async function loadPictures() {
  try {
    const pictureList = await getPictures()
    const previous = pictures.value
    const nextPictures: ScreenPictureItem[] = [
      ...getBuiltInScreenPictures(),
      ...pictureList.map((item) => ({
        ...item,
        url: URL.createObjectURL(item.blob),
      })),
    ]
    pictures.value = nextPictures
    pages.value = refreshPageDisplay(pages.value, {
      ...getPageDisplayOptions(),
      pictures: nextPictures,
    })
    revokePictureUrls(previous)
  } catch (err) {
    console.error('Failed to get picture list')
    defaultReportError(`Failed to get picture list: ${JSON.stringify(err)}`)
  }
}

/**
 * 确保当前底座设备的屏显共享数据已加载。
 * 同一设备多次调用会复用同一个 Promise，避免 pages / player 重复请求。
 */
async function ensureInitialized(force = false) {
  const deviceStore = useDeviceStore()
  const device = deviceStore.currentDevice

  if (!device?.features.baseScreen || device.manifest.type !== 'base') {
    return
  }

  if (!force && initializedDeviceId === device.id && initPromise) {
    return initPromise
  }

  if (initializedDeviceId !== device.id) {
    reset(device.manifest)
    initializedDeviceId = device.id
  }

  initPromise = (async () => {
    loading.value = true
    try {
      loadHomePresets()
      await Promise.all([loadActiveHomePresetStyle(), loadPictures()])
      await syncPages()
    } finally {
      loading.value = false
    }
  })()

  return initPromise
}

/**
 * 底座屏显共享状态。
 * pages / player 路由通过该 composable 复用同一份数据和初始化流程。
 */
export function useBaseState() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  if (!deviceWatcherStarted) {
    deviceWatcherStarted = true

    watch(
      () => currentDevice.value?.id,
      (deviceId) => {
        const device = currentDevice.value

        if (!deviceId || device?.manifest.type !== 'base' || !device.features.baseScreen) {
          reset()
          initializedDeviceId = null
          initPromise = null
          return
        }

        if (deviceId !== initializedDeviceId) {
          void ensureInitialized()
        }
      },
      { immediate: true },
    )
  }

  return {
    /** 屏显页面列表（含 UI 展示字段）。 */
    pages,
    /** 当前设备的屏显槽位策略。 */
    pageConfig,
    /** 首页预设资源列表。 */
    homePresets,
    /** 当前生效的首页固件样式值。 */
    activeHomePresetStyle,
    /** 本地素材库。 */
    pictures,
    /** 当前选中的屏显槽位。 */
    activePage,
    /** 初始化或写入设备时的 loading。 */
    loading,
    /** 确保共享数据已为当前设备完成加载。 */
    ensureInitialized,
    /** 从设备回读并刷新 pages。 */
    syncPages,
    /** 进入页面或写入设备后，重新拉取最新 pages。 */
    refreshPages,
    /** 从 manifest 加载预设图列表。 */
    loadHomePresets,
    /** 读取设备当前首页固件样式值。 */
    loadActiveHomePresetStyle,
    /** 从 IndexedDB 加载素材库并刷新缩略图。 */
    loadPictures,
    /** 获取 enrich / refresh pages 所需的展示上下文。 */
    getPageDisplayOptions,
  }
}
